import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Modal } from "react-bootstrap";
import KpiService from "../../Services/KpiService"; // adjust path
import Menubar from "../Menubar";
import { store } from "../../App";
import { Lottie } from "lottie-react";
import dashboardAnimation from "../../assets/dashboard.json";
import { GiProgression } from "react-icons/gi";
import { HiUserGroup } from "react-icons/hi";
import { MdAddComment, MdOutlineAccountBalanceWallet, MdPendingActions } from "react-icons/md";
import { FaDownload, FaEye, FaFileExcel, FaFilePdf } from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// import * as XLSX from 'xlsx';
import * as XLSX from "xlsx-js-style";
import { downloadpdf, exportToExcel } from "../utils/commonFunctions";
import { AiOutlineSearch } from "react-icons/ai";
import FeedbackBookViewer from "./FeedbackBookViewer";
import { backgroundColor } from "@mui/system";
/* ------------------------------------------------------------------
   API response (res.data.SDESC) – one object per feedback:
   dept_id, deptcode, dept_name, name_of_topic ("1".."4"), topic_des,
   is_finalsubmit (true = submitted, false = draft), district_short_name,
   f_name, l_name, designation, cfms_id, mobile, current_posting,
   inserted_time, updated_time ...
------------------------------------------------------------------- */

// colours are assigned to topics in order
const TOPIC_COLORS = ["#1f5fbf", "#4aa3f0", "#1fa58a", "#6a4fc8", "#f59e0b", "#e11d48", "#0891b2", "#84cc16"];
const DONUT_TOPIC_COLORS = [
  { color: "#FF5C5C", dark: "#C62828" }, // Coral Red
  { color: "#38BDF8", dark: "#0369A1" }, // Sky Blue
  { color: "#7C3AED", dark: "#4C1D95" }, // Vivid Violet
  { color: "#D4E157", dark: "#827717" }, // Lime Chartreuse
];

/* ---------- small helper functions (not components) ---------- */

// local yyyy-mm-dd
const dayKey = (d) => {
  const x = new Date(d);
  if (isNaN(x)) return "";
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

// const fmtDateTime = (d) => {
//   const x = new Date(d);
//   return isNaN(x)
//     ? "-"
//     : x.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
// };


// make a count look clickable only when it is > 0

const fmtDateTime = (updatedTime, insertedTime, isFinalSubmit) => {
  // Not submitted → don't show any time
  if (isFinalSubmit !== true) {
    return "-";
  }

  // Submitted + updated_time exists → show updated_time
  if (updatedTime) {
    const x = new Date(updatedTime);

    return isNaN(x)
      ? "-"
      : x.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
  }

  // Submitted + updated_time is null → show inserted_time

  if (insertedTime && [null, undefined].includes(updatedTime)) {
    const x = new Date(insertedTime);

    return isNaN(x)
      ? "-"
      : x.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
  }

  return "-";
};
const linkClass = (n) => (n > 0 ? "tpd-link" : "");

/* ---------- scroll settings ---------- */
// tables scroll vertically after this height and horizontally when columns overflow
const scrollBox = { overflow: "auto", maxHeight: 340, padding: 0 };
const tableStyle = { minWidth: "max-content" };
// bar chart: minimum pixel width given to every department before the chart starts scrolling
const BAR_SLOT_PX = 64;



/* ----------------------------- Main component ----------------------------- */

export default function ThemeProgressDashboard() {
  const [records, setRecords] = useState([]);
  const [modal, setModal] = useState({ show: false, title: "", list: [] });
  const [tip, setTip] = useState(null); // chart tooltip
  const printRef = useRef(null); // wraps everything that goes into the PDF
  const userid = localStorage.getItem("username");
  const deptid = localStorage.getItem("deptid");
  const roleid = localStorage.getItem("role");
  let districtid = localStorage.getItem("districtid");
  const [data, setdata] = useState([]);
  const [searchText, setSearch] = useState("");
  // Clone the table and remove the last column (Action) if it is shown
  const [apiMessage, setApiMessage] = useState("");

  const [tableSort, setTableSort] = useState({});
  const [tableSearch, setTableSearch] = useState({ level: "", district: "", department: "", cumulative: "" });



  const lvTh = { whiteSpace: "normal", padding: "6px 4px", fontSize: 12, lineHeight: 1.25, verticalAlign: "middle" };
  const lvTd = { whiteSpace: "nowrap", padding: "9px 4px", fontSize: 12, verticalAlign: "middle" };
  // ---- Map these to your real values ----
  const topicName = modal.list[0]?.topic_des;
  const departmentcode = modal.list[0]?.deptcode;

  const department = modal.list[0]?.dept_name;
  const district = modal.list[0]?.district_name;
  const entrylevel = modal.list[0]?.level_of_entry;


  // ============================================================
  // HEADING LINES
  // ============================================================
  const getHeadingLines = () => {
    const lines = [
      `Name of the Topic: ${topicName || "-"}`,
      `Department: ${departmentcode || "-"} - ${department || "-"}`,
    ];

    if (district) {
      lines.push(`District: ${district}`);
    }
    if (entrylevel) {
      lines.push(`Entry Level: ${levelLabel(entrylevel)}`);
    }

    return lines;
  };


  // ============================================================
  // GET EXPORT TABLE
  // ============================================================
  const getExportTable = (table_id) => {
    const table = document.getElementById(table_id);
    if (!table) return null;

    const clone = table.cloneNode(true);

    // Remove Action column from export
    if (showAction) {
      clone.querySelectorAll("tr").forEach((tr) => {
        if (tr.lastElementChild) {
          tr.removeChild(tr.lastElementChild);
        }
      });
    }

    return clone;
  };


  // ============================================================
  // GET TABLE ROWS
  // ============================================================
  const getTableRows = (tbl) =>
    Array.from(tbl.rows).map((tr) =>
      Array.from(tr.cells).map((td) => td.textContent.trim())
    );


  // ============================================================
  // FILE NAME
  // ============================================================
  const fileName = () =>
    `Officer_Details_${String(topicName || "").replace(/[^\w]+/g, "_")}`;


  // ============================================================
  // EXCEL
  // ============================================================
  const exportExcel = (table_id) => {
    const tbl = getExportTable(table_id);
    if (!tbl) return;

    const rows = getTableRows(tbl);
    const headingLines = getHeadingLines();

    const colCount = rows[0].length;

    // Heading rows + blank row + table
    const aoa = [
      ...headingLines.map((line) => [line]),
      [],
      ...rows
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);


    // ----------------------------------------------------------
    // Merge heading rows across all columns
    // ----------------------------------------------------------
    ws["!merges"] = headingLines.map((_, r) => ({
      s: { r: r, c: 0 },
      e: { r: r, c: colCount - 1 }
    }));


    // ----------------------------------------------------------
    // Column widths
    // ----------------------------------------------------------
    ws["!cols"] = Array.from({ length: colCount }, (_, c) => {
      const max = Math.max(
        ...rows.map((r) => (r[c] || "").length)
      );

      return {
        wch: Math.min(Math.max(max + 2, 10), 40)
      };
    });


    // ----------------------------------------------------------
    // BOLD HEADING LINES
    // ----------------------------------------------------------
    headingLines.forEach((_, r) => {
      const cell = ws[XLSX.utils.encode_cell({
        r: r,
        c: 0
      })];

      if (cell) {
        cell.s = {
          font: {
            bold: true,
            color: {
              rgb: "000000"
            }
          },
          alignment: {
            horizontal: "left",
            vertical: "center"
          }
        };
      }
    });


    // ----------------------------------------------------------
    // BOLD TABLE HEADER
    // ----------------------------------------------------------
    const headerRowIndex = headingLines.length + 1;

    for (let c = 0; c < colCount; c++) {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex,
        c: c
      });

      const cell = ws[cellAddress];

      if (cell) {
        cell.s = {
          font: {
            bold: true,
            color: {
              rgb: "000000"
            }
          },

          fill: {
            fgColor: {
              rgb: "FFFFFF"
            }
          },

          alignment: {
            horizontal: "center",
            vertical: "center"
          },

          border: {
            top: {
              style: "thin",
              color: {
                rgb: "000000"
              }
            },
            bottom: {
              style: "thin",
              color: {
                rgb: "000000"
              }
            },
            left: {
              style: "thin",
              color: {
                rgb: "000000"
              }
            },
            right: {
              style: "thin",
              color: {
                rgb: "000000"
              }
            }
          }
        };
      }
    }


    // ----------------------------------------------------------
    // WORKBOOK
    // ----------------------------------------------------------
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Officers"
    );

    XLSX.writeFile(
      wb,
      `${fileName()}.xlsx`
    );
  };


  // ============================================================
  // PDF
  // ============================================================
  const exportPDF = (table_id) => {
    const tbl = getExportTable(table_id);
    if (!tbl) return;

    const rows = getTableRows(tbl);
    const headingLines = getHeadingLines();


    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4"
    });


    // ----------------------------------------------------------
    // PDF HEADINGS
    // ----------------------------------------------------------
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);

    let y = 35;

    headingLines.forEach((line) => {
      doc.text(line, 40, y);
      y += 18;
    });


    // ----------------------------------------------------------
    // PDF TABLE
    // ----------------------------------------------------------
    autoTable(doc, {
      head: [rows[0]],
      body: rows.slice(1),

      startY: y + 6,

      styles: {
        font: "helvetica",
        fontSize: 8,
        fontStyle: "normal",
        cellPadding: 4,

        overflow: "linebreak",

        // BLACK TEXT
        textColor: [0, 0, 0],

        // WHITE BACKGROUND
        fillColor: [255, 255, 255],

        // BLACK BORDER
        lineColor: [0, 0, 0],
        lineWidth: 0.5
      },


      // --------------------------------------------------------
      // TABLE HEADER
      // --------------------------------------------------------
      headStyles: {
        font: "helvetica",
        fontStyle: "bold",

        // BLACK TEXT
        textColor: [0, 0, 0],

        // WHITE BACKGROUND
        fillColor: [255, 255, 255],

        // BLACK BORDER
        lineColor: [0, 0, 0],
        lineWidth: 0.5,

        halign: "center",
        valign: "middle"
      },


      // --------------------------------------------------------
      // BODY
      // --------------------------------------------------------
      bodyStyles: {
        font: "helvetica",
        fontStyle: "normal",

        // BLACK TEXT
        textColor: [0, 0, 0],

        // WHITE BACKGROUND
        fillColor: [255, 255, 255]
      },


      // --------------------------------------------------------
      // TABLE BORDER
      // --------------------------------------------------------
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.5
    });


    // ----------------------------------------------------------
    // SAVE PDF
    // ----------------------------------------------------------
    doc.save(
      `${fileName()}.pdf`
    );
  };


  // title        : text shown at the top of the sheet. If empty, getHeadingLines() is used (modal)
  // removeLastCol: true only for the modal table, to drop the Action column
  // table_id      : id of the <table> to export
  // title         : text shown at the top of the sheet (empty = modal heading lines)
  // fileNameParam : downloaded file name without .xlsx
  const exportTableExcel = (table_id, title = "", fileNameParam = "") => {
    const table = document.getElementById(table_id);
    if (!table) {
      
      return;
    }

    // ---------- 1. find the "Action" column by header text ----------
    const headCells = Array.from(table.querySelector("thead tr")?.cells || []);
    const actionIdx = headCells.findIndex(
      (c) => c.textContent.trim().toLowerCase() === "action"
    );

    // ---------- 2. read every row, expand colSpan, skip Action ----------
    const rows = [];
    const spans = []; // { row, from, to } for merged cells (Total row)

    Array.from(table.rows).forEach((tr, ri) => {
      const out = [];
      let c = 0; // logical column in the table
      Array.from(tr.cells).forEach((td) => {
        const span = td.colSpan || 1;
        const text = td.textContent.replace(/\s+/g, " ").trim();

        if (c !== actionIdx) {
          const from = out.length;
          out.push(text);
          for (let i = 1; i < span; i++) out.push("");
          if (span > 1) spans.push({ row: ri, from, to: from + span - 1 });
        }
        c += span;
      });
      rows.push(out);
    });

    if (!rows.length) return;

    const colCount = Math.max(...rows.map((r) => r.length));
    const headingLines = title ? [title] : getHeadingLines();
    const headerRowIndex = headingLines.length + 1; // heading rows + blank row

    // ---------- 3. dashboard tables: counts as numbers ----------
    const body = rows.map((r, ri) =>
      r.map((v) => (title && ri > 0 && /^\d+$/.test(v) ? Number(v) : v))
    );

    const ws = XLSX.utils.aoa_to_sheet([
      ...headingLines.map((line) => [line]),
      [],
      ...body,
    ]);

    // ---------- 4. merges: heading rows + Total row ----------
    ws["!merges"] = [
      ...headingLines.map((_, r) => ({
        s: { r, c: 0 },
        e: { r, c: colCount - 1 },
      })),
      ...spans.map((s) => ({
        s: { r: headerRowIndex + s.row, c: s.from },
        e: { r: headerRowIndex + s.row, c: s.to },
      })),
    ];

    // ---------- 5. column widths ----------
    ws["!cols"] = Array.from({ length: colCount }, (_, c) => {
      const max = Math.max(...rows.map((r) => String(r[c] ?? "").length));
      return { wch: Math.min(Math.max(max + 2, 10), 40) };
    });

    // ---------- 6. styles ----------
    const line = { style: "thin", color: { rgb: "000000" } };
    const border = { top: line, bottom: line, left: line, right: line };

    headingLines.forEach((_, r) => {
      const cell = ws[XLSX.utils.encode_cell({ r, c: 0 })];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: "000000" } },
          alignment: { horizontal: "left", vertical: "center" },
        };
      }
    });

    const lastRow = headerRowIndex + rows.length - 1;
    const hasFooter = !!(table.tFoot && table.tFoot.rows.length);

    for (let r = headerRowIndex; r <= lastRow; r++) {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        const isHeader = r === headerRowIndex;
        const isTotal = hasFooter && r === lastRow;

        ws[addr].s = {
          font: { bold: isHeader || isTotal, color: { rgb: "000000" } },
          alignment: {
            horizontal: isHeader || c > 1 ? "center" : "left",
            vertical: "center",
            wrapText: true,
          },
          border,
        };
      }
    }

    // ---------- 7. save ----------
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, (title || "Officers").slice(0, 31));

    const safe = (s) => String(s).replace(/[^\w\-]+/g, "_");
    const name = fileNameParam
      ? `${safe(fileNameParam)}_${dayKey(new Date())}`
      : title
        ? `${safe(title)}_${dayKey(new Date())}`
        : fileName();

    XLSX.writeFile(wb, `${name}.xlsx`);
  };
  const handleDownloadPdf = async () => {
    const root = printRef.current;
    if (!root) return;

    hideTip();
    store.dispatch({ type: "SHOW_SPINNER" });
    try {
      const canvas = await html2canvas(root, {
        scale: root.scrollHeight > 7000 ? 1 : 2,   // 2 = sharp; lower only for very tall pages
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
        onclone: (doc) => {
          const el = doc.getElementById("tpd-print-area");
          const all = (sel, fn) => el.querySelectorAll(sel).forEach(fn);
          const set = (n, prop, val) => n.style.setProperty(prop, val, "important");

          // hide the PDF button and chart tooltips
          all(".tpd-no-print, .tpd-tip", (n) => set(n, "display", "none"));
          all(".tpd-print-only", (n) => set(n, "display", "block"));

          // links look like normal text: no blue, no underline, no pointer
          all(".tpd-link, .tpd-link *, .tpd-table a", (n) => {
            set(n, "color", "inherit");
            set(n, "text-decoration", "none");
            set(n, "cursor", "default");
            set(n, "font-weight", "inherit");
          });

          // show every row/column instead of scrollbars
          all(".tpd-scroll", (n) => { set(n, "max-height", "none"); set(n, "overflow", "visible"); });
          all(".tpd-scroll thead th, .tpd-scroll tfoot td", (n) => set(n, "position", "static"));
          all(".tpd-scroll table", (n) => { set(n, "min-width", "0"); set(n, "width", "100%"); });

          // bar chart + donut legend: fit fully instead of scrolling
          all(".tpd-bar-scroll", (n) => set(n, "overflow", "visible"));
          all(".tpd-bar-scroll svg", (n) => { set(n, "width", "100%"); set(n, "height", "auto"); });
          all(".tpd-donut-legend", (n) => { set(n, "max-height", "none"); set(n, "overflow", "visible"); });
        },
      });

      // one PDF page sized exactly like the dashboard, so nothing is cut or split
      const w = canvas.width / (canvas.width > 4000 ? 1 : 2);
      const h = canvas.height / (canvas.width > 4000 ? 1 : 2);
      const pdf = new jsPDF({
        orientation: w > h ? "landscape" : "portrait",
        unit: "px",
        format: [w, h],
        hotfixes: ["px_scaling"],
      });
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, w, h);
      pdf.save(`Theme_Progress_Dashboard_${dayKey(new Date())}.pdf`);
    } catch (err) {
      
    } finally {
      store.dispatch({ type: "HIDE_SPINNER" });
    }
  };
  /* ---------- 1. load data ---------- */
  useEffect(() => {
    const params = {
      mode: "dashboarddata",
      userId: userid,
      districtId: ["2", "64", "3"].includes(roleid) ? districtid : "0",
      deptId: roleid === "8" ? "0" : deptid,
    };

    store.dispatch({ type: "SHOW_SPINNER" });
    KpiService.snccsFeedbackFormDashboard(params)
      .then((res) => {
        const d = res?.data;
        if (d?.SCODE === "01" && Array.isArray(d.SDESC)) {
          setRecords(d.SDESC);
          setApiMessage("");
        } else {
          setRecords([]);
          // {"SCODE":"02","SDESC":"NO DATA FOUND"} -> "NO DATA FOUND"
          setApiMessage(typeof d?.SDESC === "string" && d.SDESC ? d.SDESC : "NO DATA FOUND");
        }
      })
      .catch(() => {
        setRecords([]);
        setApiMessage("Unable to load data. Please try again.");
      })
      .finally(() => store.dispatch({ type: "HIDE_SPINNER" }));
  }, []);

  /* ---------- 2. group records by department ---------- */
  // const m = useMemo(() => {
  //   const today = dayKey(new Date());

  //   // districts only from submitted records
  //   const districts = [
  //     ...new Set(records.filter((r) => r.is_finalsubmit).map((r) => r.district_short_name).filter(Boolean)),
  //   ];

  //   const map = new Map();
  //   records.forEach((r) => {
  //     const id = String(r.dept_id);
  //     if (!map.has(id)) {
  //       map.set(id, {
  //         dept_id: id,
  //         deptcode: r.deptcode || id,
  //         dept_name: r.dept_name || id,
  //         topics: { 1: 0, 2: 0, 3: 0, 4: 0 },
  //         districts: {},
  //         draft: 0, submitted: 0,
  //         yesterday: 0, today: 0,
  //       });
  //     }
  //     const d = map.get(id);

  //     if (r.is_finalsubmit) {
  //       d.submitted += 1;
  //       const t = String(r.name_of_topic);
  //       if (d.topics[t] !== undefined) d.topics[t] += 1;

  //       if (r.district_short_name)
  //         d.districts[r.district_short_name] = (d.districts[r.district_short_name] || 0) + 1;

  //       if (dayKey(r.updated_time) === today) d.today += 1;
  //       else d.yesterday += 1;
  //     } else {
  //       d.draft += 1;
  //     }
  //   });

  //   const rows = [...map.values()].map((d) => ({
  //     ...d,
  //     total: d.submitted + d.draft,
  //     cumulative: d.yesterday + d.today,
  //   }));

  //   // departments with at least one submitted record (used everywhere except table 1)
  //   const subRows = rows.filter((r) => r.submitted > 0);

  //   const sum = (list, key) => list.reduce((a, r) => a + r[key], 0);

  //   return {
  //     rows,                                   // table 1 only (includes drafts)
  //     subRows,                                // everything else
  //     districts, today,
  //     totalProgress: sum(rows, "total"),      // table 1 footer
  //     submittedTotal: sum(subRows, "submitted"),
  //     cumulative: sum(subRows, "cumulative"),
  //     todayCount: sum(subRows, "today"),
  //     deptCount: subRows.length,
  //     allDeptCount: rows.length,              // KPI: departments incl. draft-only ones
  //   };
  // }, [records]);
  // topic list comes from the response (name_of_topic + topic_des)
  const topics = useMemo(() => {
    const seen = new Map();

    records.forEach((r) => {
      const id = String(r.name_of_topic ?? "").trim();
      if (!id) return;

      if (!seen.has(id)) {
        seen.set(id, {
          id,
          desc: r.topic_des || "",
          name: r.regular_session || "",
        });
      } else {
        const t = seen.get(id);
        if (!t.desc && r.topic_des) t.desc = r.topic_des;
        if (!t.name && r.regular_session) t.name = r.regular_session;
      }
    });

    return [...seen.values()]
      .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }))
      .map((t, i) => ({
        ...t,
        // label: t.name || `Regular Session${t.id}`,   // from regular_session, fallback if missing
        label: `Regular Session${t.id}`,   // from regular_session, fallback if missing
        color: TOPIC_COLORS[i % TOPIC_COLORS.length],
      }));
  }, [records]);

  const topicLabel = (id) => topics.find((t) => t.id === String(id))?.label || "-";

  const LEVELS = [
    { id: "1", value: "Secretariat" },
    { id: "2", value: "State HOD" },
    { id: "3", value: "District" },
    { id: "4", value: "Constituency" },
    { id: "5", value: "Division" },
    { id: "6", value: "Mandal" },
    { id: "7", value: "Village" },
  ];
  const LEVEL_FIELD = "level_of_entry"; // <-- set to the key you saw in the console
  const levelLabel = (v) =>
    LEVELS.find((l) => l.id === String(v ?? "").trim())?.value || "-";
  // accepts 3, "3", " 3 " or "District"; returns the level id or ""
  const levelIdOf = (v) => {
    const s = String(v ?? "").trim().toLowerCase();
    const hit = LEVELS.find((l) => l.id === s || l.value.toLowerCase() === s);
    return hit ? hit.id : "";
  };
  const m = useMemo(() => {
    const today = dayKey(new Date());

    // zeroed counter for every topic column
    const emptyTopics = () =>
      topics.reduce((acc, t) => {
        acc[t.id] = 0;
        return acc;
      }, {});

    const map = new Map();

    records.forEach((r) => {
      const id = String(r.dept_id);

      if (!map.has(id)) {
        map.set(id, {
          dept_id: id,
          deptcode: r.deptcode || id,
          dept_name: r.dept_name || id,
          entry_level: r.level_of_entry ?? "",
          entry_level_name: r.level_of_entry ? levelLabel(r.level_of_entry) : "",

          topics: emptyTopics(),   // <-- here
          districts: {},
          draft: 0,
          submitted: 0,
          yesterday: 0,
          today: 0,
        });
      }
      

      const d = map.get(id);

      if (r.is_finalsubmit) {
        d.submitted += 1;
        const topicId = String(r.name_of_topic);
        d.topics[topicId] = (d.topics[topicId] || 0) + 1;


        // if (d.topics[topicId] !== undefined) {
        //   d.topics[topicId] += 1;
        // }

        if (r.district_short_name) {
          d.districts[r.district_short_name] =
            (d.districts[r.district_short_name] || 0) + 1;
        }

        if (dayKey(r.updated_time) === today) {
          d.today += 1;
        } else {
          d.yesterday += 1;
        }
      } else {
        d.draft += 1;
      }
    });

    // =========================================================
    // DEPARTMENT ROWS
    // =========================================================

    // const rows = [...map.values()].map((d) => ({
    //   ...d,
    //   total: d.submitted + d.draft,
    //   cumulative: d.yesterday + d.today,
    // }));

    const rows = [...map.values()]
      .map((d) => ({
        ...d,
        total: d.submitted + d.draft,
        cumulative: d.yesterday + d.today,
      }))
      // keep departments in dept_id order (numeric)
      .sort((a, b) => Number(a.dept_id) - Number(b.dept_id));
    // Departments having submitted records
    const subRows = rows.filter((r) => r.submitted > 0);


    const districtMap = new Map();




    records.forEach((r) => {
      const shortName = r.district_short_name;
      if (!shortName) return;

      // key by district_id when available, otherwise fall back to the short name
      const key = String(r.district_id ?? shortName);

      if (!districtMap.has(key)) {
        districtMap.set(key, {
          district_id: r.district_id,
          district_short_name: shortName,
          district_name: r.district_name || shortName, // <-- full name field from your API
          topics: {},
          draft: 0,
          submitted: 0,
        });
      }

      const d = districtMap.get(key);

      if (r.is_finalsubmit) {
        d.submitted += 1;
        const topicId = String(r.name_of_topic);
        d.topics[topicId] = (d.topics[topicId] || 0) + 1;
        // const topicId = String(r.name_of_topic);
        // if (d.topics[topicId] !== undefined) d.topics[topicId] += 1;
      } else {
        d.draft += 1;
      }
    });

    const districtRows = [...districtMap.values()]
      .map((d) => ({ ...d, total: d.submitted + d.draft }))
      // keep districts in district_id order (numeric)
      .sort((a, b) => Number(a.district_id) - Number(b.district_id));
    // =========================================================
    // TOTALS
    // =========================================================


    // =========================================================
    // LEVEL-WISE, TOPIC-WISE (counts + records)
    // =========================================================
    const levelMap = new Map();
    LEVELS.forEach((lv) =>
      levelMap.set(lv.id, {
        level_id: lv.id,
        level_name: lv.value,
        topics: {},
        topicRecords: {},
        draftRecords: [],
        submittedRecords: [],   // NEW
        allRecords: [],
        userSet: new Set(),
        draft: 0,
        submitted: 0,
      })
    );
    // LEVELS.forEach((lv) =>
    //   levelMap.set(lv.id, {
    //     level_id: lv.id,
    //     level_name: lv.value,
    //     topics: {},          // { "1": 5, "2": 3 }  submitted count per topic
    //     topicRecords: {},    // { "1": [rec, rec], "2": [...] }  submitted records per topic
    //     draftRecords: [],    // draft records of this level
    //     allRecords: [],      // every record of this level
    //     draft: 0,
    //     submitted: 0,
    //   })
    // );
    const allUsers = new Set();

    records.forEach((r) => {
      const d = levelMap.get(levelIdOf(r.level_of_entry));
      if (!d) return; // empty or unknown level

      d.allRecords.push(r);

      // unique user by CFMS ID
      const cfms = String(r.cfms_id ?? "").trim();
      if (cfms) {
        d.userSet.add(cfms);
        allUsers.add(cfms);
      }

      if (r.is_finalsubmit) {
        d.submitted += 1;
        d.submittedRecords.push(r);   // NEW
        const t = String(r.name_of_topic);
        d.topics[t] = (d.topics[t] || 0) + 1;
        if (!d.topicRecords[t]) d.topicRecords[t] = [];
        d.topicRecords[t].push(r);
      } else {
        d.draft += 1;
        d.draftRecords.push(r);
      }

    });


    // records.forEach((r) => {
    //   const d = levelMap.get(levelIdOf(r.level_of_entry));
    //   if (!d) return; // empty or unknown level

    //   d.allRecords.push(r);

    //   if (r.is_finalsubmit) {
    //     d.submitted += 1;
    //     const t = String(r.name_of_topic);
    //     d.topics[t] = (d.topics[t] || 0) + 1;
    //     if (!d.topicRecords[t]) d.topicRecords[t] = [];
    //     d.topicRecords[t].push(r);
    //   } else {
    //     d.draft += 1;
    //     d.draftRecords.push(r);
    //   }
    // });

    const levelRows = [...levelMap.values()].map((d) => {
      const total = d.submitted + d.draft;
      return {
        ...d,
        total,
        users: d.userSet.size,
        submittedPct: total ? Math.round((d.submitted / total) * 100) : 0,
      };
    });
    // const levelRows = [...levelMap.values()].map((d) => ({
    //   ...d,
    //   total: d.submitted + d.draft,
    // }));


    const sum = (list, key) =>
      list.reduce((a, r) => a + r[key], 0);

    return {
      // Department table
      rows,
      subRows,

      // District table
      districtRows,

      today,
      deptTopicTotals: topics.reduce((acc, t) => {
        acc[t.id] = rows.reduce((s, r) => s + (r.topics[t.id] || 0), 0);
        return acc;
      }, {}),

      deptDraftTotal: sum(rows, "draft"),

      // Cumulative table totals (rows = subRows)
      yesterdayTotal: sum(subRows, "yesterday"),
      // Existing totals
      totalProgress: sum(rows, "total"),
      submittedTotal: sum(subRows, "submitted"),
      cumulative: sum(subRows, "cumulative"),
      todayCount: sum(subRows, "today"),
      pendingCount: sum(rows, "draft"),

      deptCount: subRows.length,
      allDeptCount: rows.length,

      // District totals
      districtTopicTotals: topics.reduce((acc, t) => {
        acc[t.id] = districtRows.reduce(
          (sum, r) => sum + (r.topics[t.id] || 0),
          0
        );

        return acc;
      }, {}),

      districtDraftTotal: districtRows.reduce(
        (sum, r) => sum + r.draft,
        0
      ),

      districtTotal: districtRows.reduce(
        (sum, r) => sum + r.total,
        0
      ),

      levelRows,
      levelTopicTotals: topics.reduce((acc, t) => {
        acc[t.id] = levelRows.reduce((s, r) => s + (r.topics[t.id] || 0), 0);
        return acc;
      }, {}),
      levelDraftTotal: levelRows.reduce((s, r) => s + r.draft, 0),
      // levelTotal: levelRows.reduce((s, r) => s + r.total, 0),
      levelTotal: levelRows.reduce((s, r) => s + r.total, 0),
      levelUserTotal: allUsers.size,                               // unique across all levels
      levelSubmittedTotal: levelRows.reduce((s, r) => s + r.submitted, 0),
      levelSubmittedPct: (() => {
        const t = levelRows.reduce((s, r) => s + r.total, 0);
        const s = levelRows.reduce((s, r) => s + r.submitted, 0);
        return t ? Math.round((s / t) * 100) : 0;
      })(),

    };
  }, [records, topics]);

  

  const levelStats = useMemo(() => {
    const map = new Map();
    LEVELS.forEach((lv) =>
      map.set(lv.id, {
        level_id: lv.id,
        level_name: lv.value,
        submitted: 0,
        draft: 0,
        total: 0,
        records: [],
      })
    );
    const unmatched = [];

    records.forEach((r) => {
      const l = map.get(levelIdOf(r[LEVEL_FIELD]));
      if (!l) { unmatched.push(r); return; }
      l.records.push(r);
      l.total += 1;
      if (r.is_finalsubmit) l.submitted += 1;
      else l.draft += 1;
    });

    const rows = [...map.values()];
    const sum = (k) => rows.reduce((a, r) => a + r[k], 0);

    return {
      rows,                          // one row per level
      submittedTotal: sum("submitted"),
      draftTotal: sum("draft"),
      total: sum("total"),
      unmatchedCount: unmatched.length, // records with a missing or unknown level
    };
  }, [records]);

  const openLevelTopic = (d, t) => {
    const list = d.topicRecords[t.id] || [];
    setModal({
      show: true,
      title: `${d.level_name} · ${list[0]?.topic_des || t.label}`,
      desc: "",
      list,
    });
  };

  const openLevelDraft = (d) =>
    setModal({ show: true, title: `${d.level_name} · Draft`, desc: "", list: d.draftRecords });

  const openLevelSubmitted = (d) =>
    setModal({ show: true, title: `${d.level_name} · Submitted`, desc: "", list: d.submittedRecords });
  const openLevelAll = (d) =>
    setModal({ show: true, title: `${d.level_name} · All`, desc: "", list: d.allRecords });

  // Unique, well-separated colour for every index (golden-angle hue spacing)
  const deptColor = (i) => {
    const hue = (i * 137.508) % 360;           // never repeats, neighbours differ a lot
    const light = [48, 40, 56][i % 3];         // vary lightness to separate close hues
    return `hsl(${Math.round(hue)}, 65%, ${light}%)`;
  };
  /* ---------- 3. chart geometry (bar chart + donut) ---------- */
  const chart = useMemo(() => {
    // =========================================================
    // BAR CHART (submitted only)
    // =========================================================
    const H = 200, padL = 28, padB = 28, padT = 20;
    const W = Math.max(420, padL + m.subRows.length * BAR_SLOT_PX); // grows when many departments

    // --- nice y-axis scale ---
    const dataMax = Math.max(1, ...m.subRows.map((r) => r.submitted));
    const TARGET_TICKS = 5;

    const niceStep = (rawMax, count) => {
      const rough = rawMax / count;
      const pow = Math.pow(10, Math.floor(Math.log10(rough)));
      const frac = rough / pow;
      const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
      return Math.max(1, nice * pow);
    };

    const step = niceStep(dataMax, TARGET_TICKS);
    const max = Math.max(step * 2, Math.ceil(dataMax / step) * step); // axis top, multiple of step

    const innerH = H - padB - padT;
    const slot = (W - padL) / Math.max(m.subRows.length, 1);
    const barW = Math.min(46, slot * 0.55);
    const yOf = (v) => padT + innerH - (v / max) * innerH;

    // y-axis ticks: only a handful of clean values
    const ticks = [];
    for (let v = 0; v <= max; v += step) ticks.push({ t: v, y: yOf(v) });

    const bars = m.subRows.map((r, i) => {
      const x = padL + slot * i + (slot - barW) / 2;
      let acc = 0;
      const segs = topics
        .map((t) => ({
          key: t.id,
          v: r.topics[t.id] || 0,
          color: t.color,
          label: t.label,
        }))
        .filter((s) => s.v > 0)
        .map((s) => {
          const h = (s.v / max) * innerH;
          const y = yOf(acc + s.v);
          acc += s.v;
          return { ...s, h, y };
        });
      return { row: r, x, segs, topY: yOf(r.submitted) };
    });


    // =========================================================
    // DONUT - SESSION/TOPIC WISE WITH 3D COLORS
    // =========================================================
    const R = 53;
    const C = 2 * Math.PI * R;
    let offset = 0;

    const arcs = topics.map((t, i) => {
      const value = m.subRows.reduce(
        (sum, r) => sum + (Number(r.topics[t.id]) || 0),
        0
      );

      const palette = DONUT_TOPIC_COLORS[i % DONUT_TOPIC_COLORS.length];
      const len = m.cumulative ? (value / m.cumulative) * C : 0;

      const arc = {
        key: t.id,
        label: t.label,
        value,
        color: palette.color,
        darkColor: palette.dark,
        len,
        gap: C - len,
        offset: -offset,
      };

      offset += len;
      return arc;
    });

    return {
      W, H, padL, padT, innerH, barW,
      ticks, bars, R, arcs,
    };

  }, [m, topics]);


  useEffect(() => { setSort({ key: null, dir: "asc" }); }, [modal.title]);
  const sameDept = (r, d) => String(r.dept_id) === d.dept_id;

  const openModal = (title, predicate) => { setModal({ show: true, title, list: records.filter(predicate) }) };

  const openTopic = (d, t) => {
    const list = records.filter(
      (r) => sameDept(r, d) && r.is_finalsubmit && String(r.name_of_topic) === t.id
    );
    const topicName = list[0]?.topic_des || t.label;

    setModal({
      show: true,
      title: `${d.deptcode} · ${topicName}`,
      desc: "",
      list,
    });
  };


  const openAllTopic = (t) => {
    const list = records.filter(
      r => r.is_finalsubmit && String(r.name_of_topic) === String(t.id)
    );

    setModal({
      show: true,
      title: `All Departments · ${list[0]?.topic_des || t.label}`,
      desc: "",
      list,
    });
  };




  // const openDistrictTopic = (d, t) => {
  //   const list = records.filter(
  //     (r) =>
  //       r.district_short_name === d.district_name &&
  //       r.is_finalsubmit &&
  //       String(r.name_of_topic) === String(t.id)
  //   );

  //   const topicName = list[0]?.topic_des || t.label;

  //   setModal({
  //     show: true,
  //     title: `${d.district_name} · ${topicName}`,
  //     desc: "",
  //     list,
  //   });
  // };

  // const openDistrictDraft = (d) =>
  //   openModal(
  //     `${d.district_name} · Draft`,
  //     (r) =>
  //       r.district_short_name === d.district_name &&
  //       !r.is_finalsubmit
  //   );

  // const openDistrictAll = (d) =>
  //   openModal(
  //     `${d.district_name} · All`,
  //     (r) =>
  //       r.district_short_name === d.district_name
  //   );


  const openDistrictTopic = (d, t) => {
    const list = records.filter(
      (r) =>
        r.district_id === d.district_id &&
        r.is_finalsubmit &&
        String(r.name_of_topic) === String(t.id)
    );
    const topicName = list[0]?.topic_des || t.label;

    setModal({
      show: true,
      title: `${d.district_name} · ${topicName}`,
      desc: "",
      list,
    });
  };

  const openDistrictDraft = (d) =>
    openModal(
      `${d.district_name} · Draft`,
      (r) => r.district_id === d.district_id && !r.is_finalsubmit
    );

  const openDistrictAll = (d) =>
    openModal(
      `${d.district_name} · All`,
      (r) => r.district_id === d.district_id
    );
  const openDistTopic = (d, t) => {
    const list = records.filter(
      (r) => sameDept(r, d) && r.is_finalsubmit && String(r.name_of_topic) === t.id
    );
    const topicName = list[0]?.topic_des || t.label;

    setModal({
      show: true,
      title: `${d.district_name} · ${topicName}`,
      desc: "",
      list,
    });
  };
  // table 1 only (includes drafts)
  const openDraft = (d) => openModal(`${d.deptcode} · Draft`, (r) => sameDept(r, d) && !r.is_finalsubmit);
  const openAll = (d) => openModal(`${d.deptcode} · All`, (r) => sameDept(r, d));

  // everything else: submitted only
  const openSubmitted = (d) =>
    openModal(`${d.deptcode} · Submitted`, (r) => sameDept(r, d) && r.is_finalsubmit);
  const openDistrict = (d, dist) =>
    openModal(`${d.deptcode} · ${dist}`, (r) => sameDept(r, d) && r.is_finalsubmit && r.district_short_name === dist);
  const openCumulative = (d) =>
    openModal(`${d.deptcode} · Cumulative`, (r) => sameDept(r, d) && r.is_finalsubmit);
  const openYesterday = (d) =>
    openModal(`${d.deptcode} · Yesterday`, (r) => sameDept(r, d) && r.is_finalsubmit && dayKey(r.updated_time) !== m.today);
  const openToday = (d) =>
    openModal(`${d.deptcode} · Today`, (r) => sameDept(r, d) && r.is_finalsubmit && dayKey(r.updated_time) === m.today);

  /* ---------- 5. chart tooltip ---------- */
  // e.currentTarget = the hovered rect/circle; tooltip sits above the chart (svg) top
  const showTip = (e, lines) => {
    const top = e.currentTarget.ownerSVGElement.getBoundingClientRect().top;
    setTip({ x: e.clientX, top, lines });
  };
  const hideTip = () => setTip(null);




  const todayLabel = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const shortDate = new Date().toLocaleDateString("en-GB");
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yesterdayLabel = yesterday.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", });
  const yesterdayShortDate = yesterday.toLocaleDateString("en-GB");
  const showAction = (modal?.list || []).some((o) => o.is_finalsubmit);

  const datacard = [
    { label: "Themes", value: m.totalProgress },
    // { label: "Submissions", value: m.submittedTotal },
    // { label: "Not Submitted", value: Number(m.totalProgress) - Number(m.submittedTotal) },
  ];


  //  const renderCard = (title, subtitle, data, HeaderIcon, colors) => (
  //   <Col lg={3} md={6} xs={12}>
  //     <div
  //       style={{
  //         background: "#fafffd",
  //         border: `1px solid ${colors.border}`,
  //         borderRadius: 12,
  //         overflow: "hidden",
  //         height: "100%",
  //         fontFamily: "system-ui",
  //         boxShadow: "0 3px 10px #0000000a",
  //       }}
  //     >
  //       <div
  //         style={{
  //           background: colors.header,
  //           color: "#fff",
  //           padding: "9px 10px",
  //           display: "flex",
  //           alignItems: "center",
  //           justifyContent: "center",
  //           gap: 7,
  //           fontWeight: 700,
  //           fontSize: 15,
  //         }}
  //       >
  //         <HeaderIcon size={17} />
  //         {title}
  //       </div>

  //       <div style={{ padding: 12, display: "flex", gap: 10, alignItems: "center" }}>
  //         <div
  //           style={{
  //             width: 42,
  //             height: 42,
  //             flexShrink: 0,
  //             borderRadius: 10,
  //             background: `linear-gradient(135deg, ${colors.icon}, ${colors.iconDark})`,
  //             display: "grid",
  //             placeItems: "center",
  //             color: "#fff",
  //           }}
  //         >
  //           <HeaderIcon size={21} />
  //         </div>

  //         <div style={{ flex: 1 }}>
  //           <div
  //             style={{
  //               color: colors.header,
  //               fontSize: 14,
  //               fontWeight: 700,
  //               paddingBottom: 5,
  //               borderBottom: `1px solid ${colors.border}`,
  //             }}
  //           >
  //             {subtitle}
  //           </div>

  //           {/* {data.map((item, i) => ( */}
  //           <div
  //             // key={item.label}
  //             style={{
  //               display: "flex",
  //               justifyContent: "space-between",
  //               padding: "5px 0",
  //               fontSize: 17,
  //               fontWeight: 600,
  //               color: colors.header
  //               // borderBottom: `1px solid ${colors.border}`
  //               // i < data.length - 1 ? `1px solid ${colors.border}` : 0,
  //             }}
  //           >
  //             <span>{data}</span>
  //           </div>
  //           {/* ))} */}
  //         </div>
  //       </div>
  //     </div>
  //   </Col>
  // );

  const renderCard = (title, subtitle, data, HeaderIcon, colors) => (
    <Col lg={3} md={6} xs={12}>
      <div
        style={{
          background: "#fafffd",
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          overflow: "hidden",
          height: "100%",
          fontFamily: "system-ui",
          boxShadow: "0 3px 10px #0000000a",
        }}
      >
        <div
          style={{
            background: colors.header,
            color: "#fff",
            padding: "9px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          <HeaderIcon size={17} />
          {title}
        </div>

        <div style={{ padding: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <div
            style={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${colors.icon}, ${colors.iconDark})`,
              display: "grid",
              placeItems: "center",
              color: "#fff",
            }}
          >
            <HeaderIcon size={21} />
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                color: colors.header,
                fontSize: 14,
                fontWeight: 700,
                paddingBottom: 5,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              {subtitle}
            </div>

            {/* {data.map((item, i) => ( */}
            <div
              // key={item.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "5px 0",
                fontSize: 17,
                fontWeight: 600,
                color: colors.header
                // borderBottom: `1px solid ${colors.border}`
                // i < data.length - 1 ? `1px solid ${colors.border}` : 0,
              }}
            >
              <span>{data}</span>
            </div>
            {/* ))} */}
          </div>
        </div>
      </div>
    </Col>
  );
  // lowercase + remove everything except letters/digits
  // "Regular Session-1" -> "regularsession1", "Regular Session1" -> "regularsession1"
  const norm = (v) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const [sort, setSort] = useState({ key: null, dir: "asc" }); // dir: "asc" | "desc"

  const filteredData = useMemo(() => {
    const q = norm(searchText);
    if (!q) return modal.list;

    return (modal.list || []).filter((o) => {
      if (!o) return false;

      // the same values that are displayed in the officer table
      const searchable = [
        o.cfms_id,
        `${o.f_name || ""} ${o.l_name || ""}`,        // full name
        o.designation,
        levelLabel(o.level_of_entry),                  // "District", "Mandal"...
        o.deptcode,
        o.dept_name,
        o.district_name,
        o.mobile,
        o.current_posting,
        topicLabel(o.name_of_topic),                   // "Regular Session1"
        o.topic_des,
        o.is_finalsubmit ? "Submitted" : "Draft",
        fmtDateTime(o.updated_time, o.inserted_time, o.is_finalsubmit),
      ];

      return searchable.some((v) => norm(v).includes(q));
    });
  }, [modal.list, searchText, topics]);
  // const filteredData = searchText.length > 0 && modal?.list?.length ? modal.list.filter((item) => item && Object.keys(item).some((key) => { const value = item[key]; return ( value !== null && value.toString().toLowerCase().includes(searchText.toLowerCase()) ); }) ) : modal.list;

  // before


  // after
  // value used to sort each column (same data the column displays)
  const SORT_VALUE = {
    sno: (o) => o.__idx,                                         // original order
    cfms_id: (o) => o.cfms_id,
    name: (o) => `${o.f_name || ""} ${o.l_name || ""}`.trim(),
    designation: (o) => o.designation,
    level: (o) => Number(levelIdOf(o.level_of_entry)) || null,   // Secretariat -> Village order
    dept: (o) => `${o.deptcode || ""} ${o.dept_name || ""}`.trim(),
    district: (o) => o.district_name,
    mobile: (o) => o.mobile,
    posting: (o) => o.current_posting,
    session: (o) => Number(o.name_of_topic) || null,
    status: (o) => (o.is_finalsubmit ? "Submitted" : "Draft"),
    time: (o) => {
      if (o.is_finalsubmit !== true) return null;                // shown as "-"
      const t = new Date(o.updated_time ?? o.inserted_time).getTime();
      return isNaN(t) ? null : t;
    },
  };

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

  const sortedData = useMemo(() => {
    const base = (filteredData || []).map((o, i) => ({ ...o, __idx: i }));
    const getVal = SORT_VALUE[sort.key];
    if (!getVal) return base;

    const dir = sort.dir === "asc" ? 1 : -1;

    return base.sort((a, b) => {
      const x = getVal(a);
      const y = getVal(b);
      const xe = x === null || x === undefined || x === "";
      const ye = y === null || y === undefined || y === "";

      if (xe && ye) return 0;
      if (xe) return 1;          // empty values always go last
      if (ye) return -1;

      if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
      return collator.compare(String(x), String(y)) * dir;
    });
  }, [filteredData, sort]);

  // click: asc -> desc -> no sort
  const toggleSort = (key) =>
    setSort((s) =>
      s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }
    );

  // sortable header cell
  // const sortTh = (key, label) => (
  //   <th
  //     className={`tpd-sortable ${sort.key === key ? `tpd-sort-${sort.dir}` : ""}`}
  //     onClick={() => toggleSort(key)}
  //     title="Click to sort"
  //   >
  //     {label}
  //   </th>
  // );

  const sortTh = (key, label) => {
    const state = sort.key === key ? sort.dir : ""; // "", "asc" or "desc"
    return (
      <th
        className="tpd-sortable"
        onClick={() => toggleSort(key)}
        title="Click to sort"
      >
        <span className="tpd-th-inner">
          {label}
          <span className={`tpd-sort-icon ${state}`}>
            <i className="up" />
            <i className="down" />
          </span>
        </span>
      </th>
    );
  };
  // use this everywhere the modal is closed, so sort and search reset
  const closeModal = () => {
    setSearch("");
    setSort({ key: null, dir: "asc" });
    setModal((m) => ({ ...m, show: false }));
  };

  const sortBy = (table, key) => setTableSort(p => ({ ...p, [table]: { key, dir: p[table]?.key === key ? -p[table].dir : 1 } }));
  const sortRows = (rows, table, getValue) => {
    const s = tableSort[table];
    return [...(rows || [])].sort((a, b) => {
      if (!s) return 0;
      const x = getValue(a, s.key), y = getValue(b, s.key);
      return (typeof x === "number" && typeof y === "number" ? x - y : String(x ?? "").localeCompare(String(y ?? ""))) * s.dir;
    });
  };

  useEffect(() => {
    const handleDownload = (e) => {
      if (e.detail) downloadpdf(e.detail);
    };

    window.addEventListener("feedback-book-download", handleDownload);

    return () => {
      window.removeEventListener("feedback-book-download", handleDownload);
    };
  }, []);

  const filterRows = (rows, table, getValues) => {
    const search = tableSearch[table].trim().toLowerCase();
    if (!search) return rows || [];

    return (rows || []).filter(row =>
      getValues(row).some(value =>
        String(value ?? "").toLowerCase().includes(search)
      )
    );
  };

  if (apiMessage) {
    return (
      <>
        <Menubar />
        <Container fluid style={{ width: "95%" }}>
          <div className="sa-title">
            <h2>
              SNCCS Feedback Form<span> Dashboard</span>
            </h2>
          </div>
        </Container>

        <Container fluid style={{ width: "95%" }} className="outer-page-content-container">
          <Row className="justify-content-center mt-4">
            <Col lg={4} md={6} xs={12}>
              <div
                style={{
                  background: "#fafffd",
                  border: "1px solid #E8C9B8",
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow: "0 3px 10px #0000000a",
                  fontFamily: "system-ui",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    background: "#A05A38",
                    color: "#fff",
                    padding: "9px 10px",
                    fontWeight: 700,
                    fontSize: 15,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 7,
                  }}
                >
                  <MdPendingActions size={18} />
                  Dashboard
                </div>

                <div style={{ padding: "28px 16px" }}>
                  <MdPendingActions size={42} color="#D89570" />
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#A05A38",
                    }}
                  >
                    {apiMessage}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  // sortable header for the dashboard tables (independent per table name)
const dashSortTh = (table, key, label, style) => {
  const s = tableSort[table];
  const state = s?.key === key ? (s.dir === 1 ? "asc" : "desc") : "";
  return (
    <th key={key} style={style} className="tpd-sortable" onClick={() => sortBy(table, key)} title="Click to sort">
      <span className="tpd-th-inner">
        {label}
        <span className={`tpd-sort-icon ${state}`}>
          <i className="up" />
          <i className="down" />
        </span>
      </span>
    </th>
  );
};
  return (
    <>
      <Menubar />
      {/* keeps header and total rows visible while the tables scroll; change the colour to match your theme */}
      <style>{`
        .tpd-scroll thead th { position: sticky; top: 0; z-index: 2; background: #eaf0fa; }
        .tpd-scroll tfoot td { position: sticky; bottom: 0; z-index: 2; background: #eaf0fa; }

        /* ---------- PDF / print ---------- */
        .tpd-print-only { display: none; }
        @page { size: A4 landscape; margin: 8mm; }
        @media print {
          html, body { background: #fff !important; height: auto !important; overflow: visible !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

          /* shrink the dashboard to fit the page width (raise for bigger text, lower to fit more) */
          #tpd-print-area { zoom: 0.72; }

          .tpd-no-print, .tpd-tip { display: none !important; }
          .tpd-print-only { display: block !important; text-align: right; font-size: 12px; color: #555; margin: 2px 0 6px; }

          /* show all rows and columns instead of scrollbars */
          .tpd-scroll { max-height: none !important; overflow: visible !important; }
          .tpd-scroll thead th, .tpd-scroll tfoot td { position: static !important; }
          .tpd-scroll table { min-width: 0 !important; width: 100% !important; }
          .tpd-bar-scroll { overflow: visible !important; }
          .tpd-bar-scroll svg { width: 100% !important; height: auto !important; }
          .tpd-donut-legend { max-height: none !important; overflow: visible !important; }

          /* avoid cutting cards and rows in half across pages */
          .tpd-panel, .tpd-kpi, .tpd-chart-card, tr { break-inside: avoid; page-break-inside: avoid; }
          thead { display: table-header-group; }
        }
      `}</style>
      <div ref={printRef} id="tpd-print-area">
        <Container fluid style={{ width: "97%" }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="sa-title">
              <h2>
                SNCCS Feedback Form<span> Dashboard</span>
              </h2>
            </div>

          </div>
          {/* <div className="tpd-print-only">Generated on {fmtDateTime(new Date())}</div> */}
        </Container>

        <Container fluid style={{ width: "97%" }} className="outer-page-content-container">

          <Row className="g-2 align-items-center">
            <Col lg={3} md={3} className="d-flex justify-content-center">
              <Lottie src={dashboardAnimation} autoplay loop style={{ width: 200, height: 200 }} />
            </Col>

            <Col lg={9} md={9}>
              <Row className="g-2 h-100">
                {renderCard("Total Theme Progress", "Count", m.totalProgress, GiProgression, {
                  header: "#176B5B",
                  icon: "#5BB7A3",
                  iconDark: "#3C9A86",
                  border: "#B8DED5",
                })}

                {renderCard("Departments Involved", "Count", m.allDeptCount, HiUserGroup, {
                  header: "#365486",
                  icon: "#6F91C4",
                  iconDark: "#5276AB",
                  border: "#C5D3E8",
                })}

                {renderCard("Submitted Cumulative Count", "Count", m.cumulative, MdAddComment, {
                  header: "#654A7E",
                  icon: "#9A79B5",
                  iconDark: "#805F9C",
                  border: "#D8CCE2",
                })}

                {renderCard(`Today's Count (${todayLabel})`, "Count", m.todayCount, MdOutlineAccountBalanceWallet, {
                  header: "#A05A38",
                  icon: "#D89570",
                  iconDark: "#BE704C",
                  border: "#E8C9B8",
                })}
              </Row>
            </Col>
          </Row>



          {/* <div className="tpd-panel">
  <div className="tpd-panel-title">Level-wise Count</div>
  <div className="tpd-panel-body tpd-scroll" style={scrollBox}>
    <table className="tpd-table" style={tableStyle}>
      <thead>
        <tr>
          <th>sno</th>
          <th>Level</th>
          <th>Submitted</th>
          <th>Draft</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        {levelStats.rows.map((r, i) => (
          <tr key={r.level_id}>
            <td>{i + 1}</td>
            <td style={{ textAlign: "left" }}>{r.level_name}</td>
            <td className={linkClass(r.submitted)} onClick={() => r.submitted > 0 && openLevelStat(r, "submitted")}>{r.submitted}</td>
            <td className={linkClass(r.draft)} onClick={() => r.draft > 0 && openLevelStat(r, "draft")}>{r.draft}</td>
            <td className={linkClass(r.total)} onClick={() => r.total > 0 && openLevelStat(r, "all")}>{r.total}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2}>Total</td>
          <td style={{ textAlign: "center" }}>{levelStats.submittedTotal}</td>
          <td style={{ textAlign: "center" }}>{levelStats.draftTotal}</td>
          <td style={{ textAlign: "center" }}>{levelStats.total}</td>
        </tr>
      </tfoot>
    </table>
  </div>
</div> */}
          {/* ================= Two progress tables ================= */}
          <Row className="g-1 mb-3">

            {/* ---- Cumulative theme items (submitted only) ---- */}
            <Col xs={12} lg={6} >


              <div className="tpd-panel">
                <div className="tpd-panel-title tpd-title-flex">
                  <span>Theme Progress by Level of Entry</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                      <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                      <input type="search" value={tableSearch.level} onChange={e => setTableSearch(p => ({ ...p, level: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                    </div>
                    <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportTableExcel("tpd_level_table", "Theme Progress by Level of Entry", "ThemeProgressbyLevelReport")} > <FaFileExcel style={{ color: "#198754" }} /> </button>
                  </div>
                </div>

                <div className="tpd-panel-body tpd-scroll" style={scrollBox}>
                  <table
                    className="tpd-table"
                    id="tpd_level_table"
                    style={{ width: "100%", minWidth: 0, tableLayout: "auto" }}
                  >
                    {/* <thead>
                      <tr>
                        <th style={lvTh}>S.No</th>
                        <th style={lvTh}>Level of Entry</th>
                        <th style={lvTh}>Registered Users</th>
                        {topics.map((t) => (
                          <th key={t.id} style={lvTh}>{t.label}</th>
                        ))}
                        <th style={lvTh}>Submitted<br />Count</th>
                        <th style={lvTh}>Draft</th>
                        <th style={lvTh}>Total</th>
                        <th style={lvTh}>Submitted %</th>
                      </tr>
                    </thead> */}
                    <thead>
  <tr>
    <th style={lvTh}>S.No</th>
    {dashSortTh("level", "level_name", "Level of Entry", lvTh)}
    {dashSortTh("level", "users", "Registered Users", lvTh)}
    {topics.map((t) => dashSortTh("level", t.id, t.label, lvTh))}
    {dashSortTh("level", "submitted", <>Submitted<br />Count</>, lvTh)}
    {dashSortTh("level", "draft", "Draft", lvTh)}
    {dashSortTh("level", "total", "Total", lvTh)}
    {dashSortTh("level", "submittedPct", "Submitted %", lvTh)}
  </tr>
</thead>

                    <tbody>
                      {(() => {
                        // const levelData = filterRows(m?.levelRows, "level", (r) => [
                        //   r.level_name,
                        //   r.users,
                        //   ...topics.map((t) => r.topics?.[t.id]),
                        //   r.draft,
                        //   r.submitted,         
                        //   r.total,
                        //   `${r.submittedPct}%`,
                        // ]);
const levelData = sortRows(
  filterRows(m?.levelRows, "level", (r) => [
    r.level_name, r.users,
    ...topics.map((t) => r.topics?.[t.id]),
    r.draft, r.submitted, r.total, `${r.submittedPct}%`,
  ]),
  "level",
  (r, key) =>
    ["level_name", "users", "submitted", "draft", "total", "submittedPct"].includes(key)
      ? r[key]
      : r.topics?.[key] ?? 0
);

                        if (levelData.length === 0) {
                          return (
                            <tr>
                              <td className="text-center" colSpan={7 + topics.length}>
                                <b style={{ color: "red" }}>Details Not Found</b>
                              </td>
                            </tr>
                          );
                        }

                        return levelData.map((r, i) => (
                          <tr key={r.level_id}>
                            <td style={lvTd}>{i + 1}</td>
                            <td style={{ ...lvTd, whiteSpace: "normal" }} title={r.level_name}>{r.level_name}</td>
                            <td style={lvTd}>{r.users}</td>

                            {topics.map((t) => (
                              <td
                                key={t.id}
                                style={lvTd}
                                className={linkClass(r.topics[t.id] || 0)}
                                onClick={() => r.topics[t.id] > 0 && openLevelTopic(r, t)}
                              >
                                {r.topics[t.id] || 0}
                              </td>
                            ))}
                            <td style={lvTd} className={linkClass(r.submitted)} onClick={() => r.submitted > 0 && openLevelSubmitted(r)} > {r.submitted} </td>
                            <td style={lvTd} className={linkClass(r.draft)} onClick={() => r.draft > 0 && openLevelDraft(r)}>
                              {r.draft}
                            </td>

                            <td style={lvTd} className={linkClass(r.total)} onClick={() => r.total > 0 && openLevelAll(r)}>
                              {r.total}
                            </td>
                            <td style={lvTd}>{r.submittedPct}%</td>
                          </tr>
                        ));
                      })()}
                    </tbody>

                    {m?.levelRows?.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan={2} style={lvTd}>Total</td>
                          <td style={{ ...lvTd, textAlign: "center" }}>{m.levelUserTotal}</td>
                          {topics.map((t) => (
                            <td key={t.id} style={{ ...lvTd, textAlign: "center" }}>
                              {m.levelTopicTotals[t.id] || 0}
                            </td>
                          ))}
                          <td style={{ ...lvTd, textAlign: "center" }}>{m.levelSubmittedTotal}</td>
                          <td style={{ ...lvTd, textAlign: "center" }}>{m.levelDraftTotal}</td>
                          {/* NEW */}
                          <td style={{ ...lvTd, textAlign: "center" }}>{m.levelTotal}</td>
                          <td style={{ ...lvTd, textAlign: "center" }}>{m.levelSubmittedPct}%</td>

                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>


                {/* <div className="tpd-panel-body tpd-scroll" style={scrollBox}>
                  <table className="tpd-table" id="tpd_level_table" style={tableStyle}>
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Level of Entry</th>
                        {topics.map((t) => (
                          <th key={t.id}>{t.label}</th>
                        ))}
                        <th>Draft</th>
                        <th>Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filterRows(m?.levelRows, "level", r => [
                        r.level_name,
                        ...topics.map(t => r.topics?.[t.id]),
                        r.draft,
                        r.total
                      ]).length === 0 ? (
                        <tr>
                          <td className="text-center" colSpan={4 + topics.length}>
                            <b style={{ color: "red" }}>Details Not Found</b>
                          </td>
                        </tr>
                      ) : (
                        filterRows(m?.levelRows, "level", r => [
                          r.level_name,
                          ...topics.map(t => r.topics?.[t.id]),
                          r.draft,
                          r.total
                        ]).map((r, i) => (
                          <tr key={r.level_id}>
                            <td>{i + 1}</td>
                            <td title={r.level_name}>{r.level_name}</td>

                            {topics.map((t) => (
                              <td
                                key={t.id}
                                className={linkClass(r.topics[t.id] || 0)}
                                onClick={() => r.topics[t.id] > 0 && openLevelTopic(r, t)}
                              >
                                {r.topics[t.id] || 0}
                              </td>
                            ))}

                            <td className={linkClass(r.draft)} onClick={() => r.draft > 0 && openLevelDraft(r)}>
                              {r.draft}
                            </td>
                            <td className={linkClass(r.total)} onClick={() => r.total > 0 && openLevelAll(r)}>
                              {r.total}
                            </td>
                          </tr>
                        )))}
                    </tbody>
                    {m?.levelRows?.length > 0 &&
                      <tfoot>
                        <tr>
                          <td colSpan={2}>Total</td>
                          {topics.map((t) => (
                            <td key={t.id} style={{ textAlign: "center" }}>
                              {m.levelTopicTotals[t.id] || 0}
                            </td>
                          ))}
                          <td style={{ textAlign: "center" }}>{m.levelDraftTotal}</td>
                          <td style={{ textAlign: "center" }}>{m.levelTotal}</td>
                        </tr>
                      </tfoot>
                    }
                  </table>
                </div> */}
              </div>
            </Col>
            {/* ---- Department-wise district progress (submitted only) ---- */}
            <Col xs={12} lg={6} >

              <div className="tpd-panel">
                {/* <div className="tpd-panel-title">Theme Progress by District</div> */}
                <div className="tpd-panel-title tpd-title-flex">
                  <span>Theme Progress by District</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                      <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                      <input type="search" value={tableSearch.district} onChange={e => setTableSearch(p => ({ ...p, district: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                    </div>
                    <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportTableExcel("district", "Theme_Progress_by_District", "ThemeProgressbyDistrictReport")}>
                      <FaFileExcel style={{ color: "#198754" }} />
                    </button>
                  </div>
                </div>
                <div className="tpd-panel-body tpd-scroll" style={scrollBox}>

                  <table className="tpd-table" id="district" style={tableStyle}>
                    {/* <thead>
                      <tr>
                        <th>S.No</th>
                        <th>District<br /> Name</th>

                        {topics.map((t) => (
                          <th key={t.id}>{t.label}</th>
                        ))}

                        <th>Draft</th>
                        <th>Total</th>
                      </tr>
                    </thead> */}
<thead>
  <tr>
    <th>S.No</th>
    {dashSortTh("district", "district_short_name", <>District<br /> Name</>)}
    {topics.map((t) => dashSortTh("district", t.id, t.label))}
    {dashSortTh("district", "draft", "Draft")}
    {dashSortTh("district", "total", "Total")}
  </tr>
</thead>
                    {/* <tbody>
                      {filterRows(m?.districtRows, "district", r => [
                        r.district_name,
                        r.district_short_name,
                        ...topics.map(t => r.topics?.[t.id]),
                        r.draft,
                        r.total
                      ]).length === 0 ? (
                        <tr>
                          <td className="text-center" colSpan={4 + topics.length}>
                            <b style={{ color: "red" }}>Details Not Found</b>
                          </td>
                        </tr>
                      ) : (
                        filterRows(m?.districtRows, "district", r => [
                          r.district_name,
                          r.district_short_name,
                          ...topics.map(t => r.topics?.[t.id]),
                          r.draft,
                          r.total
                        ]).map((r, i) => (
                          <tr key={r.district_id}> */}
                          <tbody>
  {(() => {
    const distData = sortRows(
      filterRows(m?.districtRows, "district", (r) => [
        r.district_name, r.district_short_name,
        ...topics.map((t) => r.topics?.[t.id]),
        r.draft, r.total,
      ]),
      "district",
      (r, key) =>
        ["district_short_name", "draft", "total"].includes(key)
          ? r[key]
          : r.topics?.[key] ?? 0
    );

    if (distData.length === 0)
      return (
        <tr>
          <td className="text-center" colSpan={4 + topics.length}>
            <b style={{ color: "red" }}>Details Not Found</b>
          </td>
        </tr>
      );

    return distData.map((r, i) => (
      <tr key={r.district_id}>
                            <td>{i + 1}</td>

                            <td title={r.district_name}>
                              {r.district_short_name}
                            </td>

                            {topics.map((t) => (
                              <td
                                key={t.id}
                                className={linkClass(r.topics[t.id] || 0)}
                                onClick={() =>
                                  r.topics[t.id] > 0 &&
                                  openDistrictTopic(r, t)
                                }
                              >
                                {r.topics[t.id] || 0}
                              </td>
                            ))}

                            {/* Draft */}
                            <td
                              className={linkClass(r.draft)}
                              onClick={() =>
                                r.draft > 0 &&
                                openDistrictDraft(r)
                              }
                            >
                              {r.draft}
                            </td>

                            {/* Total */}
                            <td
                              className={linkClass(r.total)}
                              onClick={() =>
                                r.total > 0 &&
                                openDistrictAll(r)
                              }
                            >
                              {r.total}
                            </td>
                          </tr>
                        // )))}
                        ));
  })()}
                    </tbody>
                    {m?.districtRows?.length > 0 &&
                      <tfoot>
                        <tr>
                          <td colSpan={2}>
                            Total
                          </td>

                          {topics.map((t) => (
                            <td key={t.id} style={{ textAlign: "center" }}>
                              {m.districtTopicTotals[t.id] || 0}
                            </td>
                          ))}

                          <td style={{ textAlign: "center" }}>
                            {m.districtDraftTotal}
                          </td>

                          <td style={{ textAlign: "center" }} >
                            {m.districtTotal}
                          </td>
                        </tr>
                      </tfoot>
                    }
                  </table>

                </div>
              </div>
            </Col>
          </Row>

          {/* ================= Cumulative table + charts ================= */}
          <Row className="g-1 mb-3">
            {/* ---- Theme progress by department (includes drafts) ---- */}
            <Col xs={12} lg={6} >

              <div className="tpd-panel">
                <div className="tpd-panel-title tpd-title-flex">
                  <span>Theme Progress by Department</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                      <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                      <input type="search" value={tableSearch.department} onChange={e => setTableSearch(p => ({ ...p, department: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                    </div>
                    <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportTableExcel("department", "Theme Progress by Department", "ThemeProgressbyDepartmentReport")}>
                      <FaFileExcel style={{ color: "#198754" }} />
                    </button>
                  </div>
                </div>
                <div className="tpd-panel-body tpd-scroll" style={scrollBox}>
                  <table className="tpd-table" id="department" style={tableStyle}>
                    <thead>
                      <tr>
                        <th>S.No</th>

                        {[
                          ["deptcode", "Dept Name"],
                          ...topics.map(t => [t.id, t.label]),
                          ["draft", "Draft"],
                          ["total", "Total"]
                        ].map(([key, label]) => (
                          <th key={key} className="tpd-sortable" onClick={() => sortBy("department", key)} title="Click to sort">
                            <span className="tpd-th-inner">
                              {label}
                              <span className={`tpd-sort-icon ${tableSort.department?.key === key ? tableSort.department.dir === 1 ? "asc" : "desc" : ""}`}>
                                <i className="up" />
                                <i className="down" />
                              </span>
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {sortRows(
                        filterRows(m?.rows, "department", r => [
                          r.dept_name,
                          r.deptcode,
                          ...topics.map(t => r.topics?.[t.id]),
                          r.draft,
                          r.total
                        ]),
                        "department",
                        (r, key) =>
                          ["deptcode", "draft", "total"].includes(key)
                            ? r[key]
                            : r.topics?.[key] ?? 0
                      ).length === 0 ? (
                        <tr>
                          <td className="text-center" colSpan={4 + topics.length}>
                            <b style={{ color: "red" }}>Details Not Found</b>
                          </td>
                        </tr>
                      ) : (
                        sortRows(
                          filterRows(m?.rows, "department", r => [
                            r.dept_name,
                            r.deptcode,
                            ...topics.map(t => r.topics?.[t.id]),
                            r.draft,
                            r.total
                          ]),
                          "department",
                          (r, key) =>
                            ["deptcode", "draft", "total"].includes(key)
                              ? r[key]
                              : r.topics?.[key] ?? 0
                        ).map((r, i) => (
                          <tr key={r.dept_id}>
                            <td>{i + 1}</td>
                            <td title={r.dept_name}>{r.deptcode}</td>

                            {topics.map(t => (
                              <td
                                key={t.id}
                                className={linkClass(r.topics?.[t.id] || 0)}
                                onClick={() => r.topics?.[t.id] > 0 && openTopic(r, t)}
                              >
                                {r.topics?.[t.id] || 0}
                              </td>
                            ))}

                            <td className={linkClass(r.draft)}
                              onClick={() => r.draft > 0 && openDraft(r)}>
                              {r.draft}
                            </td>

                            <td className={linkClass(r.total)}
                              onClick={() => r.total > 0 && openAll(r)}>
                              {r.total}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    {!!m?.rows?.length && (
                      <tfoot>
                        <tr>
                          <td colSpan={2}>Total</td>
                          {topics.map(t => <td key={t.id} style={{ textAlign: "center" }}>{m.deptTopicTotals[t.id] || 0}</td>)}
                          <td style={{ textAlign: "center" }}>{m.deptDraftTotal}</td>
                          <td style={{ textAlign: "center" }}>{m.totalProgress}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </Col>

            <Col xs={12} lg={6} >

              <div className="tpd-panel">
                <div className="tpd-panel-title tpd-title-flex">
                  <span>Submitted Cumulative Theme Items</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                      <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                      <input type="search" value={tableSearch.cumulative} onChange={e => setTableSearch(p => ({ ...p, cumulative: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                    </div>
                    <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportTableExcel("cumulative", "Submitted Cumulative Theme Items", "SubmittedCumulativeThemeItemsReport")}>
                      <FaFileExcel style={{ color: "#198754" }} />
                    </button>
                  </div>
                </div>
                {/* <div className="tpd-panel-title">Submitted Cumulative Theme Items</div> */}
                <div className="tpd-panel-body tpd-scroll" style={scrollBox}>
                  <table className="tpd-table" id="cumulative" style={tableStyle}>
                    {/* <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Dept Name</th>
                        <th>Cumulative Count<br />Upto ({yesterdayShortDate})</th>
                        <th>Today ({shortDate})</th>
                        <th>Total</th>
                      </tr>
                    </thead> */}
                    <thead>
  <tr>
    <th>S.No</th>
    {dashSortTh("cumulative", "deptcode", "Dept Name")}
    {dashSortTh("cumulative", "yesterday", <>Cumulative Count<br />Upto ({yesterdayShortDate})</>)}
    {dashSortTh("cumulative", "today", `Today (${shortDate})`)}
    {dashSortTh("cumulative", "cumulative", "Total")}
  </tr>
</thead>
                    {/* <tbody>
                      {filterRows(m?.subRows, "cumulative", r => [
                        r.dept_name,
                        r.deptcode,
                        r.yesterday,
                        r.today,
                        r.cumulative
                      ]).length === 0 ? (
                        <tr>
                          <td className="text-center" colSpan={5}>
                            <b style={{ color: "red" }}>Details Not Found</b>
                          </td>
                        </tr>
                      ) : (
                        filterRows(m?.subRows, "cumulative", r => [
                          r.dept_name,
                          r.deptcode,
                          r.yesterday,
                          r.today,
                          r.cumulative
                        ]).map((r, i) => (
                          <tr key={r.dept_id}> */}
                          <tbody>
  {(() => {
    const cumData = sortRows(
      filterRows(m?.subRows, "cumulative", (r) => [
        r.dept_name, r.deptcode, r.yesterday, r.today, r.cumulative,
      ]),
      "cumulative",
      (r, key) => r[key]
    );

    if (cumData.length === 0)
      return (
        <tr>
          <td className="text-center" colSpan={5}>
            <b style={{ color: "red" }}>Details Not Found</b>
          </td>
        </tr>
      );

    return cumData.map((r, i) => (
      <tr key={r.dept_id}>
                            <td>{i + 1}</td>
                            <td title={r.dept_name}>{r.deptcode}</td>

                            <td className={linkClass(r.yesterday)}
                              onClick={() => r.yesterday > 0 && openYesterday(r)}>
                              {r.yesterday}
                            </td>

                            <td className={linkClass(r.today)}
                              onClick={() => r.today > 0 && openToday(r)}>
                              {r.today}
                            </td>

                            <td className={linkClass(r.cumulative)}
                              onClick={() => r.cumulative > 0 && openCumulative(r)}>
                              {r.cumulative}
                            </td>
                          </tr>
                      //   ))
                      // )}
                      ));
  })()}
                    </tbody>

                    {m.subRows.length > 0 &&
                      <tfoot>
                        <tr>
                          <td colSpan={2}>Total</td>
                          <td style={{ textAlign: "center" }}>{m.yesterdayTotal}</td>
                          <td style={{ textAlign: "center" }}>{m.todayCount}</td>
                          <td style={{ textAlign: "center" }}>{m.cumulative}</td>
                        </tr>
                      </tfoot>
                    }
                  </table>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={12} >
              <div className="tpd-panel">
                <div className="tpd-panel-title">Visual Overview</div>
                <div className="tpd-panel-body">
                  <Row className="g-2">
                    {/* ---- stacked bar chart ---- */}
                    <Col xs={12} md={8}>
                      <div className="tpd-chart-card">
                        <div className="tpd-chart-title">Theme Progress by Department</div>
                        <div className="tpd-bar-scroll" style={{ overflowX: "auto", overflowY: "hidden" }}>
                          <svg
                            viewBox={`0 0 ${chart.W} ${chart.H}`}
                            width={chart.W > 420 ? Math.round(chart.W * (210 / chart.H)) : "100%"}
                            height={210}
                          >
                            {/* grid lines + y axis numbers */}
                            {chart.ticks.map((tk) => (
                              <g key={tk.t}>
                                <line x1={chart.padL} x2={chart.W} y1={tk.y} y2={tk.y} stroke="#e5e9f0" />
                                <text x={chart.padL - 6} y={tk.y + 3} fontSize="9" textAnchor="end" fill="#555">{tk.t}</text>
                              </g>
                            ))}

                            {/* one stacked bar per department */}
                            {chart.bars.map((b) => (
                              <g key={b.row.dept_id}>
                                {b.segs.map((s) => (
                                  <g key={s.key}>
                                    <rect
                                      className="tpd-hit"
                                      x={b.x} y={s.y} width={chart.barW} height={s.h} fill={s.color}
                                      onMouseMove={(e) =>
                                        showTip(e, [
                                          { head: `Dept ID: ${b.row.deptcode}` },
                                          { sub: b.row.dept_name },
                                          { color: s.color, label: s.label, value: s.v },
                                        ])
                                      }
                                      onMouseLeave={hideTip}
                                      onClick={() => {
                                        hideTip();
                                        openTopic(b.row, topics.find((t) => t.id === s.key));
                                      }}
                                    />
                                    {s.h > 12 && (
                                      <text
                                        x={b.x + chart.barW / 2}
                                        y={s.y + s.h / 2 + 3}
                                        fontSize="9"
                                        fill="#fff"
                                        textAnchor="middle"
                                        pointerEvents="none"
                                      >
                                        {s.v}
                                      </text>
                                    )}
                                  </g>
                                ))}
                                {/* submitted total above bar, dept code below */}
                                <text x={b.x + chart.barW / 2} y={b.topY - 4} fontSize="10" fontWeight="700" textAnchor="middle">{b.row.submitted}</text>
                                <text x={b.x + chart.barW / 2} y={chart.H - 12} fontSize="9" textAnchor="middle" fill="#333">{b.row.deptcode}</text>
                              </g>
                            ))}
                            <text x={(chart.W + chart.padL) / 2} y={chart.H} fontSize="9" textAnchor="middle" fill="#555">Department ID</text>
                          </svg>
                        </div>
                        <div className="tpd-legend">
                          {topics.map((t) => (
                            <span key={t.id}><i style={{ background: t.color }} />{t.label}</span>
                          ))}
                        </div>
                      </div>
                    </Col>

                    {/* ---- donut chart ---- */}


                    <Col xs={12} md={4}>
                      <div className="tpd-chart-card">
                        <div className="tpd-chart-title">Submitted Cumulative Theme Items</div>

                        <div style={{ height: 170, filter: "drop-shadow(0 5px 4px #173b7320)" }}>
                          <svg viewBox="0 0 170 170" width="100%" height="100%">
                            <defs>
                              {chart.arcs.map((a, i) => (
                                <linearGradient key={a.key} id={`donutGrad${i}`}>
                                  <stop stopColor={a.color} />
                                  <stop offset="100%" stopColor={a.darkColor} />
                                </linearGradient>
                              ))}
                            </defs>

                            {[6, 0].map((depth) => (
                              <g key={depth} transform={`translate(0 ${depth}) rotate(-90 85 82)`}>
                                {depth === 0 && (
                                  <circle cx="85" cy="82" r={chart.R} fill="none"
                                    stroke="#eef1f6" strokeWidth="25" />
                                )}
                                {chart.arcs.map((a, i) => (
                                  <circle key={a.key} cx="85" cy="82" r={chart.R} fill="none"
                                    stroke={depth ? a.darkColor : `url(#donutGrad${i})`}
                                    strokeWidth="25" strokeDasharray={`${a.len} ${a.gap}`}
                                    strokeDashoffset={a.offset}
                                    onMouseMove={depth ? undefined : e => showTip(e, [
                                      { head: a.label },
                                      { color: a.color, label: "Submitted", value: a.value },
                                      { label: "Percentage", value: `${m.cumulative ? Math.round(a.value / m.cumulative * 100) : 0}%` }
                                    ])}
                                    onMouseLeave={depth ? undefined : hideTip}
                                    className={depth ? "" : "tpd-hit"}

                                    onClick={depth ? undefined : () => {
                                      hideTip();
                                      const topic = topics.find(t => String(t.id) === String(a.key));
                                      if (topic) openAllTopic(topic);
                                    }}

                                  />
                                ))}
                              </g>
                            ))}

                            {chart.arcs.map(a => {
                              const p = m.cumulative ? a.value / m.cumulative * 100 : 0;
                              const angle = (-90 + (-a.offset + a.len / 2) / (2 * Math.PI * chart.R) * 360) * Math.PI / 180;
                              return p > 0 && (
                                <text key={a.key}
                                  x={85 + chart.R * Math.cos(angle)}
                                  y={82 + chart.R * Math.sin(angle)}
                                  fill="#fff" fontSize="9" fontWeight="700"
                                  textAnchor="middle" dominantBaseline="middle"
                                  pointerEvents="none">
                                  {Math.round(p)}%
                                </text>
                              );
                            })}

                            <text x="85" y="80" textAnchor="middle" fontSize="22"
                              fontWeight="700" fill="#173b73" pointerEvents="none">
                              {chart.arcs.reduce((s, a) => s + a.value, 0)}
                            </text>
                            <text x="85" y="94" textAnchor="middle" fontSize="8"
                              fill="#555" pointerEvents="none">Total Submitted</text>
                          </svg>
                        </div>

                        <div className="tpd-legend tpd-legend-grid tpd-donut-legend"
                          style={{ maxHeight: 96, overflowY: "auto" }}>
                          {chart.arcs.map(a => (
                            <span key={a.key} title={a.label}>
                              <i style={{ background: a.color, borderRadius: "50%" }} />
                              {a.label} {a.value} ({m.cumulative ? Math.round(a.value / m.cumulative * 100) : 0}%)
                            </span>
                          ))}
                        </div>
                      </div>
                    </Col>


                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          {/* <Row className="g-3 mb-2">
            <div className="d-flex justify-content-center align-items-center">
              <button
                type="button"
                className="btn btn-sm btn-danger tpd-no-print"
                onClick={handleDownloadPdf}
                title="Save this dashboard as PDF"
                style={{ textAlign: "center" }}
              >
                <FaFilePdf />Generate PDF
              </button>
            </div>
          </Row> */}
          {/* ================= Chart tooltip (one for both charts) ================= */}
          {tip && (
            <div
              className="tpd-tip tpd-no-print"
              style={{
                left: Math.min(Math.max(tip.x, 120), window.innerWidth - 120),
                top: Math.max(tip.top - 8, 90),
              }}
            >
              {tip.lines.map((l, i) =>
                l.head ? (
                  <div key={i} className="tpd-tip-head">{l.head}</div>
                ) : l.sub ? (
                  <div key={i} className="tpd-tip-sub">{l.sub}</div>
                ) : (
                  <div key={i} className={`tpd-tip-row ${l.total ? "tpd-tip-total" : ""}`}>
                    <span>{l.color && <i style={{ background: l.color }} />}{l.label}</span>
                    <span>{l.value}</span>
                  </div>
                )
              )}
            </div>
          )}

          {/* ================= Officer details popup ================= */}
          <Modal show={modal.show} onHide={closeModal} size="xl" centered dialogClassName="tpd-modal"

          >
            <div className="tpd-modal-header">
              <h5 className="tpd-modal-title">
                Officer Details – {modal.title}
                <span className="tpd-modal-count">{modal.list.length}</span>
              </h5>
              <button type="button" className="tpd-modal-close" onClick={closeModal}>&#215;</button>
              {/* <button type="button" className="tpd-modal-close" onClick={() => {setSearch("");setModal({ ...modal, show: false })}}>
                &#215;
              </button> */}
            </div>

            <div className="tpd-modal-body">
              {modal.list?.length === 0 ? (
                <div className="tpd-empty">No officer records found.</div>
              ) : (
                <>
                  <Row className="mt-2">
                    <Col xs={12} sm={12} md={3} lg={3} xl={3} xxl={3} className=" mb-1">
                      <div style={{ display: "flex", alignItems: "center", border: "1px solid #ddd", borderRadius: "20px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", transition: "all 0.3s ease-in-out", backgroundColor: "#fff", padding: "5px 10px", width: "150px", minWidth: "150px", }} onFocus={(e) => { e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.2)"; e.currentTarget.style.border = "1px solid #353935"; e.currentTarget.style.width = "100%"; }} onBlur={(e) => { e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)"; e.currentTarget.style.border = "1px solid #ddd"; e.currentTarget.style.width = "150px"; }} >
                        <AiOutlineSearch className="me-2" style={{ fontSize: "1.2rem", color: "#888", visibility: "visible", }} />
                        <input type="text" placeholder="Search" value={searchText} onChange={(event) => setSearch(event.target.value)} style={{ flex: "1", border: "none", outline: "none", fontSize: "1rem", color: "#333", backgroundColor: "transparent", transition: "color 0.3s ease", minWidth: "0", }} />
                      </div>

                    </Col>
                    <Col xs={12} sm={12} md={9} lg={9} xl={9} xxl={9} className=" mb-1 " >
                      <button type="button" className="tpd-export-btn tpd-excel-btn" style={{ float: "right" }} onClick={() => exportExcel("Officer_Details")} > <FaFileExcel className="me-1" /> Excel </button>

                      <button type="button" className="tpd-export-btn tpd-pdf-btn me-2" style={{ float: "right" }} onClick={() => exportPDF("Officer_Details")} > <FaFilePdf className="me-1" /> PDF </button>
                    </Col>
                  </Row>

                  <div className="tpd-table-wrapper">
                    <table className="tpd-table tpd-officers" id="Officer_Details">

                      <thead>
                        <tr>
                          {/* {sortTh("sno", "sno")} */}
                          <th>S.No</th>
                          {sortTh("cfms_id", "CFMS ID")}
                          {sortTh("name", "Officer Name")}
                          {sortTh("designation", "Designation")}
                          {sortTh("level", "Entry Level")}
                          {sortTh("dept", "Department")}
                          {sortTh("district", "District")}
                          {sortTh("mobile", "Mobile")}
                          {sortTh("posting", "Current Posting")}
                          {sortTh("session", "Session")}
                          {sortTh("status", "Status")}
                          {sortTh("time", <>Submitted <br />Time</>)}
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!sortedData || sortedData.length === 0 ? (
                          <tr>
                            <td className="text-center" colSpan="13">
                              <b style={{ color: "red" }}>Details Not Found</b>
                            </td>
                          </tr>
                        ) : (
                          sortedData.map((o, i) => (
                            <tr key={`${o.sno ?? o.__idx}-${o.cfms_id}`}>
                              <td>{i + 1}</td>
                              <td>{o.cfms_id || "-"}</td>
                              <td>{`${o.f_name || ""} ${o.l_name || ""}`.trim() || "-"}</td>
                              <td>{o.designation || "-"}</td>
                              <td>{levelLabel(o.level_of_entry) || "-"}</td>
                              <td>{o.deptcode}<br />{o.dept_name || "-"}</td>
                              <td>{o.district_name || "-"}</td>
                              <td>{o.mobile || "-"}</td>
                              <td>{o.current_posting || "-"}</td>
                              <td title={o.topic_des}>{topicLabel(o.name_of_topic)}</td>
                              <td>
                                <span className={`tpd-badge ${o.is_finalsubmit ? "tpd-badge-final" : "tpd-badge-draft"}`}>
                                  {o.is_finalsubmit ? "Submitted" : "Draft"}
                                </span>
                              </td>
                              <td>
                                {fmtDateTime(
                                  o.updated_time,
                                  o.inserted_time,
                                  o.is_finalsubmit
                                )}
                              </td>
                              {/* <td>{fmtDateTime(o.updated_time)}</td> */}
                              {/* {showAction && ( */}
                              <td>
                                {/* {o.is_finalsubmit && ( */}
                                <div className="d-flex align-items-center gap-2">
                                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadpdf(o, true)} title="View" > <FaEye /> </button>
                                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadpdf(o)} title="Download" > <FaDownload /> </button>

                                </div> {/* )} */}
                              </td>
                              {/* )} */}
                            </tr>
                          )))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="tpd-modal-footer text-end">
              <button type="button" className="btn btn-sm btn-secondary rounded-3" onClick={closeModal}>Close</button>
              {/* <button type="button" className="btn btn-sm btn-secondary rounded-3" onClick={() => {setSearch("");setModal({ ...modal, show: false })}}>
                Close
              </button> */}
            </div>
          </Modal>
          <FeedbackBookViewer />
        </Container>
      </div>
    </>
  );
}
